import Loader from 'components/shared/spinner';
import { GET_ARTICLES } from 'lib/queries';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import sanityGraphQLClient from 'utils/sanityGraphQLClient';

import { ArticleMeta } from './ArticleMeta';

const StyledRelatedArticles = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 3em 0em;

  h1 {
    font-weight: 600;
    font-size: 2rem;
  }

  @media screen and (min-width: 567px) {
    h1 {
      font-size: 3rem;
    }
  }

  .article {
    display: grid;
    gap: 1em;
    grid-template-columns: 90px auto;
    height: 120px;
    width: 100%;
    align-items: center;
    background: white;
    padding: 0.85em;
    border: 1px solid rgba(255, 0, 0, 0.1);
    border-radius: 8px;
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);

    &:not(:last-child) {
      margin-bottom: 1em;
    }

    .title {
      font-size: 1.5rem;
    }

    .cover-image {
      width: 100%;
      overflow: hidden;
      height: 100%;
      border-radius: 8px;
      position: relative;

      .img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
      }
    }

    .meta {
      color: black;
      font-size: 1.15rem;

      &:last-child {
        color: ${(props) => props.theme.primaryLink};
      }
    }

    @media screen and (min-width: 567px) {
      grid-template-columns: 120px auto;
      height: 150px;

      .title {
        font-size: 2rem;
      }

      .meta {
        font-size: 1.25rem;
      }
    }

    @media screen and (min-width: 991px) {
      grid-template-columns: 150px auto;
      height: 170px;
      gap: 1.5em;

      .title {
        font-size: 2rem;
      }

      .meta {
        font-size: 1.25rem;
      }
    }
  }
`;

export const RelatedArticles = ({ article }) => {
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isFetchingArticles, setIsFetchingArticles] = useState(false);

  useEffect(async () => {
    if (isFetchingArticles) return;
    setIsFetchingArticles(true);

    try {
      const response = await sanityGraphQLClient(GET_ARTICLES, {
        where: {
          slug: {
            current: {
              neq: article.slug.current
            }
          },
          tags: {
            matches: article.slug.tags
          }
        },
        sort: [{ publishedAt: 'DESC' }],
        limit: 5,
        offset: 0
      });

      setRelatedArticles(response?.allPost || []);
    } catch (error) {
      console.log(error);
    }

    setIsFetchingArticles(false);
  }, []);

  return (
    <StyledRelatedArticles>
      <h1>Related articles</h1>

      {isFetchingArticles ? (
        <Loader />
      ) : (
        <div className="articles">
          {relatedArticles.map((relatedArticle, i) => {
            return (
              <Link href={`/blog/${article.slug?.current}`} key={`related-article-${i}`}>
                <a className="article">
                  <div className="cover-image">
                    <Image
                      alt={article?.title}
                      src={article.coverImage?.asset?.url}
                      layout="fill"
                      className="."
                    />
                  </div>

                  <div>
                    <h2 className="title">{relatedArticle.title}</h2>
                    <ArticleMeta
                      author={article.author}
                      date={article._updatedAt}
                      detailClassName="meta"
                    />
                  </div>
                </a>
              </Link>
            );
          })}
        </div>
      )}
    </StyledRelatedArticles>
  );
};
